/*
 * Trace storefront recorder for Shopify.
 * Captures a privacy-safe DOM replay plus a compact activity timeline.
 * Input values are always masked and sensitive areas can opt out with
 * data-trace-block or data-trace-mask.
 */
(function traceTracker(config) {
  const eventEndpoint = config && config.endpoint ? config.endpoint : '/api/events';
  const replayEndpoint = config && config.replayEndpoint
    ? config.replayEndpoint
    : eventEndpoint.replace(/\/events(?:\?.*)?$/, '/replay');
  const consentRequired = config && config.consentRequired !== false;
  const sessionKey = 'trace_session_id';
  const visitorKey = 'trace_visitor_id';
  const startedAt = Date.now();
  const randomId = globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function'
    ? globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    : Math.random().toString(36).slice(2, 18);
  const sessionId = sessionStorage.getItem(sessionKey) || `sess-${randomId}`;
  sessionStorage.setItem(sessionKey, sessionId);
  const visitorId = localStorage.getItem(visitorKey) || `visitor-${randomId}`;
  localStorage.setItem(visitorKey, visitorId);

  let started = false;
  let recording = false;
  let pendingCustomer = null;
  let idle = false;
  let idleTimer = null;
  let replayTimer = null;
  let heartbeatTimer = null;
  let replayEvents = [];
  let pendingRequests = [];
  let retryTimer = null;
  let eventSequence = 0;
  let cartSnapshot = { cartValue: 0, cartCount: 0, lineCount: 0, currency: '', items: [] };
  const semanticSentAt = new Map();

  function allowed() {
    if (!consentRequired || window.__TRACE_CONSENT__ === true) return true;
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return true;
    if (typeof privacy.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed() === true;
    if (typeof privacy.userCanBeTracked === 'function') return privacy.userCanBeTracked() === true;
    return false;
  }

  function basePayload() {
    return {
      sessionId,
      visitorId,
      path: location.pathname + location.search,
      pageTitle: document.title,
      referrer: document.referrer || '',
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      device: navigator.userAgent || '',
      deviceClass: window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      locale: config.locale || document.documentElement.lang || '',
      themeId: config.themeId || '',
      themeRole: config.themeRole || 'live',
      isPreview: Boolean(config.isPreview || new URLSearchParams(location.search).has('preview_theme_id') || document.querySelector('[data-shopify-preview-bar]')),
      pageType: pageType(),
      startedAt
    };
  }

  function pageType() {
    const path = location.pathname || '/';
    if (/\/products\//i.test(path)) return 'product';
    if (/\/collections\//i.test(path)) return 'collection';
    if (/\/cart(?:\.js)?\/?$/i.test(path)) return 'cart';
    if (/\/search\/?$/i.test(path)) return 'search';
    if (/\/checkout|\/thank_you|\/orders\//i.test(path)) return 'checkout';
    return path === '/' ? 'home' : 'other';
  }

  function safeText(value, limit) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, limit || 120);
  }

  function productContext(target) {
    const node = target && target.closest ? target.closest('[data-product-handle], [data-product-id], form[action*="/cart/add"], .product-card, .productitem, .product-single') : null;
    const source = node || target;
    const handle = source?.getAttribute?.('data-product-handle')
      || source?.querySelector?.('[data-product-handle]')?.getAttribute('data-product-handle')
      || (location.pathname.match(/\/products\/([^/?]+)/i) || [])[1]
      || '';
    const variantId = source?.getAttribute?.('data-variant-id')
      || source?.querySelector?.('[name="id"]')?.value
      || '';
    const title = source?.getAttribute?.('data-product-title')
      || source?.querySelector?.('.product-single__title, .card__heading, .product-title, [data-product-title]')?.textContent
      || document.querySelector('.product-single__title, .product-title')?.textContent
      || '';
    return {
      productHandle: safeText(handle, 160),
      variantId: safeText(variantId, 40),
      productTitle: safeText(title, 160)
    };
  }

  function isTraceUrl(url) {
    return !url || url.indexOf(eventEndpoint) === 0 || url.indexOf(replayEndpoint) === 0;
  }

  function scheduleRetry() {
    if (retryTimer || !pendingRequests.length) return;
    retryTimer = setTimeout(async () => {
      retryTimer = null;
      const queue = pendingRequests.splice(0, 8);
      for (const item of queue) {
        try {
          const response = await fetch(item.url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: item.body,
            keepalive: item.body.length < 60000
          });
          if (!response.ok) throw new Error(`Trace returned ${response.status}`);
        } catch {
          if (item.attempt < 4) pendingRequests.push({ ...item, attempt: item.attempt + 1 });
        }
      }
      if (pendingRequests.length) scheduleRetry();
    }, 2200);
  }

  async function postJson(url, payload) {
    const body = JSON.stringify(payload);
    try {
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: body.length < 60000
      });
      if (!response.ok) throw new Error(`Trace returned ${response.status}`);
      return true;
    } catch {
      pendingRequests.push({ url, body, attempt: 1 });
      scheduleRetry();
      return false;
    }
  }

  function send(type, detail, extra) {
    if (!allowed()) return;
    const payload = {
      ...basePayload(),
      eventId: `${sessionId}-${Date.now()}-${++eventSequence}`,
      type,
      detail: detail || '',
      ...(extra || {})
    };
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon && body.length < 60000 && ['click', 'session_end', 'visibility_hidden'].includes(type)) {
      if (navigator.sendBeacon(eventEndpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
    }
    postJson(eventEndpoint, payload);
  }

  function heartbeat() {
    if (!started || document.hidden || !allowed()) return;
    send('heartbeat', 'Presence confirmed', { idle });
  }

  function semanticAllowed(type, label) {
    const key = `${type}:${label || ''}`;
    const now = Date.now();
    if (now - Number(semanticSentAt.get(key) || 0) < 1800) return false;
    semanticSentAt.set(key, now);
    return true;
  }

  async function refreshCartSnapshot() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        cartSnapshot = {
          cartValue: Number(cart.total_price || 0) / 100,
          cartCount: Number(cart.item_count || 0),
          lineCount: Array.isArray(cart.items) ? cart.items.length : 0,
          currency: safeText(cart.currency, 8),
          items: Array.isArray(cart.items) ? cart.items.slice(0, 12).map((item) => ({
            handle: safeText(item.handle, 160),
            variantId: safeText(item.variant_id, 40),
            quantity: Number(item.quantity || 0)
          })) : []
        };
      }
    } catch {
      // Keep the last known snapshot when Shopify's cart endpoint is unavailable.
    }
    return cartSnapshot;
  }

  async function sendCartEvent(type, label, context) {
    const key = `${context?.productHandle || ''}:${context?.variantId || ''}:${label || ''}`;
    if (!semanticAllowed(type, key)) return;
    const snapshot = await refreshCartSnapshot();
    send(type, label, { ...snapshot, ...(context || {}) });
  }

  function sendCheckoutEvent(label, extra) {
    const detail = label || 'Checkout started';
    if (!semanticAllowed('checkout_start', detail)) return;
    send('checkout_start', detail, { ...cartSnapshot, ...(extra || {}) });
  }

  function flushReplay(useBeacon) {
    clearTimeout(replayTimer);
    replayTimer = null;
    if (!replayEvents.length || !allowed()) return;
    const events = replayEvents;
    replayEvents = [];
    const payload = { ...basePayload(), events };
    const body = JSON.stringify(payload);
    if (useBeacon && navigator.sendBeacon && body.length < 60000) {
      const accepted = navigator.sendBeacon(replayEndpoint, new Blob([body], { type: 'application/json' }));
      if (accepted) return;
    }
    postJson(replayEndpoint, payload);
  }

  function queueReplay(event) {
    replayEvents.push(event);
    if (replayEvents.length >= 20) flushReplay(false);
    else if (!replayTimer) replayTimer = setTimeout(() => flushReplay(false), 1200);
  }

  function startReplay() {
    if (recording || !allowed()) return;
    const recorder = window.rrwebRecord && window.rrwebRecord.record;
    if (typeof recorder !== 'function') {
      // Replay is optional in the live theme; funnel signals continue without it.
      return;
    }
    recording = true;
    recorder({
      emit: queueReplay,
      checkoutEveryNms: 60000,
      maskAllInputs: true,
      maskTextSelector: '[data-trace-mask], [data-private], [contenteditable="true"]',
      blockSelector: '[data-trace-block], iframe, video, audio',
      recordCanvas: false,
      collectFonts: false,
      inlineImages: false,
      sampling: {
        mousemove: 80,
        touchmove: 80,
        scroll: 100,
        input: 'last'
      },
      slimDOMOptions: {
        script: true,
        comment: true,
        headFavicon: true,
        headWhitespace: true
      }
    });
  }

  function noteActivity() {
    if (!started) return;
    if (idle) {
      idle = false;
      send('activity_resume', 'Visitor resumed activity');
    }
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      idle = true;
      send('idle_start', '30 sec idle');
    }, 30000);
  }

  function traceRequest(url, method, response, error) {
    const normalized = String(url || '').split('?')[0];
    if (!started || isTraceUrl(String(url || ''))) return;
    const verb = String(method || 'GET').toUpperCase();
    const cartMatch = normalized.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/i);
    if (cartMatch && verb !== 'GET') {
      const type = cartMatch[1].toLowerCase() === 'add' ? 'add_to_cart' : 'cart_update';
      setTimeout(() => sendCartEvent(type, `Cart ${cartMatch[1].toLowerCase()}`, {
        requestMethod: verb,
        requestPath: normalized,
        requestStatus: response ? response.status : 0,
        requestOk: Boolean(response && response.ok)
      }), 350);
    }
    if (/\/(?:checkout|cart\/checkout)(?:\/|$)/i.test(normalized) && verb !== 'GET') {
      sendCheckoutEvent('Checkout started', { requestMethod: verb, requestPath: normalized, requestStatus: response ? response.status : 0 });
    }
    if (error || (response && !response.ok)) {
      send('shopify_request_error', safeText(error?.message || (response ? `HTTP ${response.status}` : 'Network request failed'), 180), {
        requestMethod: verb,
        requestPath: normalized,
        requestStatus: response ? response.status : 0
      });
    }
  }

  function installNetworkInstrumentation() {
    if (window.__TRACE_NETWORK_INSTALLED__) return;
    window.__TRACE_NETWORK_INSTALLED__ = true;
    const nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function traceFetch(input, init) {
        const url = typeof input === 'string' ? input : input?.url || '';
        const method = init?.method || input?.method || 'GET';
        return nativeFetch.apply(this, arguments).then((response) => {
          traceRequest(url, method, response);
          return response;
        }).catch((error) => {
          traceRequest(url, method, null, error);
          throw error;
        });
      };
    }
    if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
      const nativeOpen = XMLHttpRequest.prototype.open;
      const nativeSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function traceOpen(method, url) {
        this.__traceMethod = method;
        this.__traceUrl = url;
        return nativeOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function traceSend() {
        this.addEventListener('load', () => traceRequest(this.__traceUrl, this.__traceMethod, { status: this.status, ok: this.status >= 200 && this.status < 400 }));
        this.addEventListener('error', () => traceRequest(this.__traceUrl, this.__traceMethod, null, new Error('Network request failed')));
        return nativeSend.apply(this, arguments);
      };
    }
  }

  function sendInitialFunnelContext() {
    const path = location.pathname || '/';
    const query = new URLSearchParams(location.search);
    if (/\/products\//i.test(path)) {
      send('view_item', 'Product viewed', productContext(document.querySelector('main') || document.body));
    } else if (/\/collections\//i.test(path)) {
      send('collection_view', 'Collection viewed', { collectionHandle: safeText((path.match(/\/collections\/([^/?]+)/i) || [])[1], 160), sortBy: query.get('sort_by') || 'default' });
    } else if (/\/search\/?$/i.test(path)) {
      send('search', safeText(query.get('q'), 160), { query: safeText(query.get('q'), 160) });
    } else if (/\/cart\/?$/i.test(path)) {
      send('cart_view', 'Cart viewed', cartSnapshot);
    } else if (/\/thank_you|\/orders\//i.test(path)) {
      send('order_confirmation_view', 'Order confirmation viewed');
    }
  }

  function boot() {
    if (started || !allowed()) return;
    started = true;
    send('session_start', 'New session', { replayAvailable: Boolean(window.rrwebRecord && typeof window.rrwebRecord.record === 'function') });
    send('page_view', location.pathname + location.search);
    if (pendingCustomer && pendingCustomer.id) {
      send('identify', 'Customer identified', {
        customerId: pendingCustomer.id,
        customerName: pendingCustomer.name,
        customerEmail: pendingCustomer.email,
        identitySource: pendingCustomer.source
      });
    } else if (pendingCustomer && pendingCustomer.email) {
      send('identify', 'Newsletter subscriber identified', {
        customerName: pendingCustomer.name,
        customerEmail: pendingCustomer.email,
        identitySource: pendingCustomer.source || 'newsletter'
      });
    }

    ['mousemove', 'scroll', 'keydown', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, noteActivity, { passive: true });
    });
    document.addEventListener('click', (event) => {
      noteActivity();
      const target = event.target && event.target.closest
        ? event.target.closest('[data-trace-label], a, button, input[type="submit"]')
        : null;
      if (!target) return;
      const label = target.dataset.traceLabel
        || target.getAttribute('aria-label')
        || target.textContent.trim().slice(0, 80)
        || target.tagName;
      send('click', label, {
        selector: target.tagName.toLowerCase(),
        x: Math.round(event.clientX),
        y: Math.round(event.clientY),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      });
      const checkoutTarget = target.matches('[name="checkout"], [href*="/checkout"]')
        || target.closest('[name="checkout"], [href*="/checkout"]');
      if (checkoutTarget) sendCheckoutEvent('Checkout started', { checkoutSource: 'click', element: target.tagName.toLowerCase() });
      const addTarget = target.matches('[name="add"], [data-add-to-cart], [data-action="add-to-cart"], .js-grid-cart')
        || target.closest('[name="add"], [data-add-to-cart], [data-action="add-to-cart"], .js-grid-cart');
      if (addTarget) {
        const productLabel = addTarget.getAttribute('data-product-title')
          || addTarget.getAttribute('aria-label')
          || document.querySelector('.product-single__title')?.textContent?.trim()
          || document.title
          || 'Product';
        setTimeout(() => sendCartEvent('add_to_cart', productLabel, { ...productContext(addTarget), addSource: 'click' }), 700);
      }
      const cartTarget = target.matches('a[href*="/cart"], [data-cart-toggle], [aria-controls*="cart" i]')
        || target.closest('a[href*="/cart"], [data-cart-toggle], [aria-controls*="cart" i]');
      if (cartTarget) send('cart_open', 'Cart opened', { source: 'click' });
      const trackTarget = target.matches('a[href*="17track" i], [data-trace-label*="track" i]')
        || target.closest('a[href*="17track" i], [data-trace-label*="track" i]');
      if (trackTarget) send('track_package_click', 'Track package clicked');
      const subscribeTarget = target.matches('[data-vclub], [href*="subscribe" i], [data-trace-label*="subscribe" i]')
        || target.closest('[data-vclub], [href*="subscribe" i], [data-trace-label*="subscribe" i]');
      if (subscribeTarget) send('subscribe_click', 'Subscribe CTA clicked');
    }, { passive: true });

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target) return;
      if (target.matches('select[name="sort_by"], #SortBy, #SortBy-mobile, #SortBy-sidebar')) {
        send('collection_sort_change', safeText(target.value, 80), { sortBy: safeText(target.value, 80) });
      } else if (target.matches('[name="id"], select[name*="option" i], [data-variant-selector]')) {
        send('variant_change', safeText(target.value, 80), { variantId: safeText(target.value, 40), ...productContext(target) });
      }
    }, { passive: true });

    document.addEventListener('submit', (event) => {
      const form = event.target && event.target.closest ? event.target.closest('form') : null;
      if (!form) return;
      const action = String(form.getAttribute('action') || '');
      if (/\/cart\/add/i.test(action)) {
        const productLabel = form.getAttribute('data-product-title') || document.title || 'Product';
        setTimeout(() => sendCartEvent('add_to_cart', productLabel, { ...productContext(form), addSource: 'form' }), 700);
      } else if (form.querySelector('[name="checkout"]')) {
        sendCheckoutEvent('Checkout started', { checkoutSource: 'form' });
      }
      const emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
      const marker = [form.id, form.className, action, form.querySelector('[name="contact[tags]"]')?.value]
        .filter(Boolean).join(' ').toLowerCase();
      const email = String(emailInput?.value || '').trim().toLowerCase();
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && /(newsletter|subscribe|marketing)/.test(marker)) {
        const firstName = String(form.querySelector('input[name*="first_name" i]')?.value || '').trim();
        const lastName = String(form.querySelector('input[name*="last_name" i]')?.value || '').trim();
        const name = [firstName, lastName].filter(Boolean).join(' ');
        pendingCustomer = { id: '', name, email, source: 'newsletter' };
        send('identify', 'Newsletter subscriber identified', {
          customerName: name,
          customerEmail: email,
          identitySource: 'newsletter'
        });
      }
    }, { passive: true });

    let lastScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const depth = Math.min(100, Math.round((window.scrollY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight)) * 100));
      if (depth >= lastScrollDepth + 25) {
        lastScrollDepth = depth;
        send('scroll', `${depth}% depth`, { depth, viewportWidth: window.innerWidth, viewportHeight: window.innerHeight });
      }
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      send(document.hidden ? 'visibility_hidden' : 'visibility_visible', document.hidden ? 'Tab moved to background' : 'Tab visible again');
      if (!document.hidden) {
        noteActivity();
        heartbeat();
      }
    });
    window.addEventListener('error', (event) => {
      send('javascript_error', safeText(event.message || 'Unhandled storefront error', 180), {
        source: safeText(event.filename, 180),
        line: Number(event.lineno || 0),
        column: Number(event.colno || 0)
      });
    });
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason && event.reason.message ? event.reason.message : event.reason;
      send('javascript_error', safeText(reason || 'Unhandled promise rejection', 180), { source: 'unhandledrejection' });
    });
    window.addEventListener('pagehide', () => {
      clearInterval(heartbeatTimer);
      send('session_end', 'Page closed or navigated');
      flushReplay(true);
    });

    noteActivity();
    heartbeat();
    installNetworkInstrumentation();
    heartbeatTimer = setInterval(heartbeat, 10000);
    refreshCartSnapshot().then(sendInitialFunnelContext);
    startReplay();
  }

  function startWhenAllowed() {
    if (allowed()) boot();
  }

  window.Trace = {
    boot,
    status() {
      return { sessionId, visitorId, started, recording, consentAllowed: allowed(), queued: pendingRequests.length, replayQueued: replayEvents.length };
    },
    identify(customer) {
      const identity = typeof customer === 'object' ? customer : { id: customer };
      pendingCustomer = {
        id: String(identity.id || ''),
        name: String(identity.name || '').slice(0, 120),
        email: String(identity.email || '').slice(0, 180),
        source: String(identity.source || (identity.id ? 'shopify_account' : 'newsletter')).slice(0, 40)
      };
      if (started && (pendingCustomer.id || pendingCustomer.email)) {
        send('identify', 'Customer identified', {
          customerId: pendingCustomer.id,
          customerName: pendingCustomer.name,
          customerEmail: pendingCustomer.email,
          identitySource: pendingCustomer.source
        });
      }
    }
  };

  document.addEventListener('visitorConsentCollected', startWhenAllowed);
  document.addEventListener('trackingConsentAccepted', startWhenAllowed);
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], startWhenAllowed);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWhenAllowed, { once: true });
  } else {
    startWhenAllowed();
  }
})(window.TRACE_CONFIG || {});
